import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Progress, SectionHeader, Typography } from 'web-library';
import { useTranslation } from 'react-i18next/';
import queryString from 'query-string';

import { candidateService } from '../../../api';
import { useUserDetails } from '../../../hooks/userDetails';
import { Role } from '../../../models';
import { useResultNavigation } from '../../../routes/ResultRoute';
import RouteKey from '../../../routes/key';
import Blank from '../../../components/Blank';

const useStyles = makeStyles()((theme) =>
({
  examContainer: {
    backgroundColor: theme.color.background.bgAlt,
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: theme.shadow.shadow1,
    borderRadius: 16,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  examLabel: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  examDate: {
    textDecoration: 'underline',
  },
}));

export interface ExamItemProps {
  label: string;
  navigate: Function;
}

const ExamItem = ({ label, navigate }: ExamItemProps) => {
  const { classes } = useStyles();

  return (
    (<Grid container className={classes.examContainer} justify="space-between">
      <Grid xs={12} sm={6}>
        <Typography className={classes.examLabel} variant="h3">
          {label}
        </Typography>
      </Grid>
      <Button
        id={`button-resultListing-${label}`}
        onClick={() => {
          navigate();
        }}
      >
        View Result
      </Button>
    </Grid>)
  );
};

const ResultListing = ({ header, userRole }: { header: string; userRole: string }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { toDetail } = useResultNavigation();
  const { data: userDetails, isLoading: isUserDetailsLoading } = useUserDetails();

  const { coordinatorResultRelease, coordinatorResultList } = RouteKey.result.child;
  const { generic } = RouteKey.error.child;
  const { result, error: errorRoute } = RouteKey.child;

  const goDetail = useCallback(
    (detailParamsPath: string) => {
      if (userRole === 'candidate') {
        history.push(`${toDetail}?${detailParamsPath}`);
      }
      if (userRole === 'coordinator') {
        history.push(
          `/${result}/${coordinatorResultList.route}/${coordinatorResultRelease.route}`,
          { code: detailParamsPath }
        );
      }
    },
    [
      coordinatorResultList.route,
      coordinatorResultRelease.route,
      history,
      result,
      toDetail,
      userRole,
    ]
  );

  const { data, error, isLoading } = candidateService.result.candidate.useExamResultsListingQuery();

  if (isLoading || isUserDetailsLoading) {
    return <Progress color="secondary" />;
  }

  if (error) {
    if (error?.response?.status === 403) {
      history.push(`/${errorRoute}/${generic}`);
    }

    // TODO: sevin - handle unexpected error uniformly
    return (
      <Typography variant="h3">
        {t('exam->>ResultListing.An unexpected error occurred. Please try again!')}
      </Typography>
    );
  }

  if (!data?.length) {
    return (
      <Blank
        content={
          <>
            {t(
              'admin->>ResultListing.Results are currently not available. Please visit the SEAB website for results release details.'
            )}
          </>
        }
        header="View Results"
      />
    );
  }

  return (
    <>
      <SectionHeader title={header} />
      {data?.map((resultListingItem) => {
        const { examLevel, examCode, examSeriesCode, examYear } = resultListingItem;
        const navigateToDetails = () =>
          goDetail(
            queryString.stringify({
              series: examSeriesCode,
              year: examYear,
              code: examCode,
            })
          );

        if (
          userDetails?.data.organisationAccess?.find((access) =>
            access.examCode.includes(examCode)
          ) ||
          userDetails?.data.roles?.find((role) => role.includes(Role.sysAdmin))
        ) {
          return (
            <ExamItem
              key={`${examSeriesCode}-${examYear}-${examLevel}`}
              label={`${examLevel}`}
              navigate={navigateToDetails}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export default ResultListing;
